/**
 * publishScheduledContent
 *
 * Sole owner of the "publish a scheduled item" pair: the force-publish write and
 * the `APP_EVENT_KEYS.CONTENT_ITEM_UPDATED` announcement that goes with it.
 *
 * The two are one responsibility, not two steps that happen to sit together — a
 * successful publish MUST be announced with `patch: { schedule: null }` or every
 * mounted content surface keeps rendering the stale schedule
 * (`useContentDetails.ts`, `ContentDetailsPage.tsx`, `useUserMedia.ts`,
 * `UserVideosPage.tsx`, `useContentTags.ts` all listen for that event).
 *
 * Plain module rather than a hook so both call sites can use it: the content
 * actions sheet (`useContentActions.publishScheduled`) and the non-React
 * scheduled-content notification tap handler
 * (`inbox/notificationTap/handlers/scheduledContentNotificationTapHandler.ts`).
 * Sibling precedent for a non-hook presentation module: `feature/share/buildSocialShareProps.ts`.
 *
 * Callers own their own UX (sheets, toasts); this module owns only the pair.
 */

import { forcePublishContentUseCase } from '@/domain/usecases/media/forcePublishContentUseCase';
import { APP_EVENT_KEYS, eventBus } from '@/infrastructure/eventBus';

/**
 * Force-publishes a scheduled media item and, on success, announces it so every
 * mounted content surface drops the stale schedule.
 *
 * @returns whether the publish write succeeded.
 */
export const publishScheduledContent = async (mediaId: string): Promise<boolean> => {
  const success = await forcePublishContentUseCase.execute(mediaId);
  if (success) {
    eventBus.emit(APP_EVENT_KEYS.CONTENT_ITEM_UPDATED, {
      mediaId,
      patch: { schedule: null },
    });
  }
  return success;
};
